"use client";

import { useEffect, useRef } from "react";
import PageContent from "../../page-content";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface BoundingBox {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
}

interface HeroImageProps {
  imagePath: string;
  boxes: BoundingBox[];
  className?: string;
}

const HeroImage = ({ imagePath, boxes, className }: HeroImageProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;
      
      const scaledWidth = imgElement.width;
      const scaledHeight = imgElement.height;

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;
      canvas.style.width = '100%';
      canvas.style.height = '100%';

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

      // Draw bounding boxes
      const boxColor = "rgb(34, 197, 94)"; // green-500
      const transparentColor = "rgba(34, 197, 94, 0.2)";

      boxes.forEach((box) => {
        const x = box.x_min * scaledWidth;
        const y = box.y_min * scaledHeight;
        const width = (box.x_max - box.x_min) * scaledWidth;
        const height = (box.y_max - box.y_min) * scaledHeight;

        ctx.fillStyle = transparentColor;
        ctx.fillRect(x, y, width, height);

        ctx.strokeStyle = boxColor;
        ctx.lineWidth = 6;
        ctx.strokeRect(x, y, width, height);
      });
    };

    imgElement.src = imagePath;

    return () => {
      isMounted = false;
    };
  }, [imagePath, boxes]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
    />
  );
};

const Hero = () => {
  // Example detection results from the laundry image
  const laundryBoxes: BoundingBox[] = [
    { x_min: 0.396, y_min: 0.631, x_max: 0.521, y_max: 0.742 },
    { x_min: 0.507, y_min: 0.816, x_max: 0.726, y_max: 0.990 },
    { x_min: 0.024, y_min: 0.740, x_max: 0.204, y_max: 0.863 }
  ];

  const dishesBoxes: BoundingBox[] = [
    { x_min: 0.422, y_min: 0.579, x_max: 0.704, y_max: 0.906 }
  ];

  return (
    <PageContent>
      <Card className="border-none shadow-none bg-transparent">
        <CardContent className="p-0">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h1 className="text-2xl leading-tight font-extrabold lg:text-4xl lg:leading-tight">
                  Give robots the vision to see, understand, and act in the real world
                </h1>
                <p className="mt-6 text-lg font-light lg:text-xl">
                  Moondream&apos;s vision model enables robots to locate objects, navigate environments, and <span className="font-semibold text-foreground">complete tasks autonomously</span>.
                </p>
                <p className="mt-5 text-lg font-light lg:text-xl">
                  Our VLM is low latency and efficient, making it perfect for when you need reasoning 24/7. You could run approximately <span className="font-semibold text-foreground">3,370 vision queries for just $1</span> with our <Link href="/org/me/api-keys" className="text-foreground underline underline-offset-4 hover:text-primary">cloud API</Link> or <span className="font-semibold text-foreground">run for free</span> with <Link href="/station" className="text-foreground underline underline-offset-4 hover:text-primary">Moondream Station</Link>.
                </p>
                <p className="mt-5 text-lg font-light lg:text-xl">
                  Perfect for <span className="font-semibold text-foreground">humanoids</span> in the home, <span className="font-semibold text-foreground">robotic arms</span>, <span className="font-semibold text-foreground">pick and place</span> systems, <span className="font-semibold text-foreground">drones</span>, as well as <span className="font-semibold text-foreground">manufacturing</span>, <span className="font-semibold text-foreground">warehouse automation</span>, <span className="font-semibold text-foreground">agriculture</span>, and <span className="font-semibold text-foreground">service applications</span>.
                </p>
              </div>
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  document.getElementById('examples')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="self-start"
              >
                View Examples
                <ArrowDown className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Hero image cluster with 2 overlapping robot POV examples */}
            <Card className="border-none shadow-none bg-transparent overflow-visible">
              <CardContent className="p-0">
                <AspectRatio ratio={4/3}>
                  <div className="relative w-full h-full">
                    {/* Laundry scene - top left layer */}
                    <div className="absolute top-[2%] left-[3%] w-[65%] h-[55%] transform rotate-[-8deg] z-[3]">
                      <div className="relative w-full h-full rounded-2xl shadow-2xl border-4 border-white overflow-hidden bg-black">
                        <HeroImage
                          imagePath="/images/solutions/robot-vision/robot_laundry_example.jpg"
                          boxes={laundryBoxes}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    
                    {/* Kitchen sink - bottom center layer */}
                    <div className="absolute bottom-[6%] left-1/2 transform -translate-x-1/2 rotate-[5deg] w-[60%] h-[58%] z-[2]">
                      <div className="relative w-full h-full rounded-2xl shadow-2xl border-4 border-white overflow-hidden bg-black">
                        <HeroImage
                          imagePath="/images/solutions/robot-vision/robot_dishes_example.jpg"
                          boxes={dishesBoxes}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </AspectRatio>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </PageContent>
  );
};

export default Hero;

